import { useCallback, useEffect, useRef, useState } from 'react'
import BuscadorArticulo from '../../components/BuscadorArticulo'
import SelectorArticulosModal from '../../components/SelectorArticulosModal'
import ModalPegarPedido from '../../components/ModalPegarPedido'
import Boton from '../../components/Boton'
import { npApi, listasProveedorApi } from '../../api/compras'
import { colors, text, space, font, radius } from '../../design/tokens'
import { st } from '../emp900/_helpers'
import { calcularRenglon, totalesRenglones, renglonDesdeArticulo, renglonEnBlanco, renglonVacio, fmtMoneda } from './_np'

/**
 * Columnas del renglón, en el orden que fijó Martin (1 sep 2026). Las de
 * `edit:false` se calculan o vienen del maestro; al pegar desde Excel lo que
 * cae sobre ellas se ignora.
 */
const COLS = [
  { k: 'codigo', label: 'Código', w: 112, edit: true, tipo: 'texto', mono: true },
  { k: 'descripcion', label: 'Detalle', edit: false, minW: 260 },
  { k: 'precio_lista', label: 'Precio lista', w: 104, edit: true, tipo: 'importe' },
  { k: 'descuento1_pct', label: 'Dto1 %', w: 64, edit: true, tipo: 'pct' },
  { k: 'descuento2_pct', label: 'Dto2 %', w: 64, edit: true, tipo: 'pct' },
  { k: 'descuento3_pct', label: 'Dto3 %', w: 64, edit: true, tipo: 'pct' },
  { k: 'precio', label: 'Precio', edit: false, calc: 'precio', dec: 4 },
  { k: 'cantidad', label: 'Cantidad', w: 76, edit: true, tipo: 'num' },
  { k: 'iva_pct', label: 'IVA %', w: 60, edit: true, tipo: 'pct' },
  { k: 'internos_pct', label: 'Internos %', w: 76, edit: true, tipo: 'pct' },
  { k: 'total', label: 'Total', edit: false, calc: 'final' },
  { k: 'codigo_proveedor', label: 'Cód. proveedor', w: 120, edit: true, tipo: 'texto', mono: true },
  { k: 'ean', label: 'EAN', w: 130, edit: true, tipo: 'texto', mono: true },
]
const EDITABLES = COLS.map((c, i) => (c.edit ? i : -1)).filter(i => i >= 0)

// "1.234,56" / "1,234.56" / "$ 12" / "10%" → "1234.56" (lo que tipea o pega un humano desde Excel).
function normalizarNumero(v) {
  let s = String(v ?? '').trim().replace(/[$%\s]|US\$/g, '')
  if (s === '') return ''
  if (s.includes(',') && s.includes('.')) s = s.lastIndexOf(',') > s.lastIndexOf('.') ? s.replace(/\./g, '').replace(',', '.') : s.replace(/,/g, '')
  else if (s.includes(',')) s = s.replace(',', '.')
  s = s.replace(/[^\d.-]/g, '')
  return s
}
const limpiarTipeo = (tipo, v) => (tipo === 'texto' ? v : v.replace(/[^\d.,]/g, '').replace(',', '.'))

/**
 * Grilla de productos de la Nota de Pedido, tipo Excel (pedido de Martin):
 * flechas para moverse en las cuatro direcciones, Enter baja (y abre renglón
 * nuevo al final), y se puede PEGAR desde Excel: las columnas caen en el mismo
 * orden de la grilla desde la celda donde se está parado. El código se
 * resuelve contra el maestro de LGR al salir de la celda (codigo_alfa o EAN).
 * Precio y Total se calculan a la vista con el mismo cálculo del backend.
 */
export default function RenglonesNpEditor({ renglones, onChange, bonifPct = 0, moneda = 'ARS', proveedorId = null, soloLectura = false, listaVersion = 0 }) {
  const [buscador, setBuscador] = useState(false)
  const [pegar, setPegar] = useState(false)
  const [iaResultado, setIaResultado] = useState(null) // {preseleccion, avisos} de "Pegar pedido"
  const gridRef = useRef(null)
  const pedidos = useRef(new Set())          // skus ya consultados por código de proveedor

  // Siempre hay una fila en blanco al final para tipear el próximo código.
  const filas = soloLectura ? renglones : [...renglones, renglonEnBlanco()]

  const actualizar = useCallback((nuevas) => onChange(nuevas), [onChange])
  const setFila = (i, cambios) => {
    const base = i < renglones.length ? renglones : [...renglones, renglonEnBlanco()]
    actualizar(base.map((r, j) => (j === i ? { ...r, ...cambios } : r)))
  }
  const quitar = (i) => actualizar(renglones.filter((_, j) => j !== i))

  const agregar = (a) => actualizar([...renglones, renglonDesdeArticulo(a)])
  // Selección múltiple (buscador o "Pegar pedido"): cada artículo con su cantidad.
  const agregarVarios = (sel) => actualizar([...renglones, ...sel.map(({ articulo, cantidad }) => renglonDesdeArticulo(articulo, { cantidad: cantidad ? String(cantidad) : '' }))])

  // "Pegar pedido" (igual que en Presupuestos): lo que la IA resolvió precarga
  // el selector único para revisarlo; lo que no pudo matchear queda como aviso.
  const alInterpretarPedido = (lineas) => {
    setIaResultado({
      preseleccion: lineas.filter(l => l.articulo).map(l => ({ articulo: l.articulo, cantidad: l.cantidad })),
      avisos: lineas.filter(l => !l.articulo).map(l => l.texto_original),
    })
    setPegar(false)
    setBuscador(true)
  }

  /** Resuelve el código tipeado contra el maestro (exacto por codigo_alfa o EAN). */
  const resolverCodigo = useCallback(async (filasBase, i, codigo) => {
    const cod = String(codigo || '').trim()
    if (cod === '') return filasBase.map((r, j) => (j === i ? { ...r, sku: '', descripcion: '', error: '' } : r))
    if (filasBase[i]?.sku && filasBase[i].sku.toUpperCase() === cod.toUpperCase()) return filasBase
    try {
      const { data } = await npApi.buscarArticulos(cod)
      const lista = data.data || data.articulos || data || []
      const a = lista.find(x => String(x.codigo_alfa).toUpperCase() === cod.toUpperCase()) || lista.find(x => x.codigo_ean && String(x.codigo_ean) === cod)
      if (!a) return filasBase.map((r, j) => (j === i ? { ...r, sku: '', descripcion: '', error: `No existe el código ${cod}` } : r))
      return filasBase.map((r, j) => (j === i ? renglonDesdeArticulo(a, {
        cantidad: r.cantidad, precio_lista: r.precio_lista, descuento1_pct: r.descuento1_pct, descuento2_pct: r.descuento2_pct, descuento3_pct: r.descuento3_pct,
        codigo_proveedor: r.codigo_proveedor || a.codigo_pro || '', ean: r.ean || a.codigo_ean || '', error: '',
      }) : r))
    } catch {
      return filasBase.map((r, j) => (j === i ? { ...r, error: 'No se pudo consultar el maestro' } : r))
    }
  }, [])

  const alSalirDeCodigo = async (i) => {
    const fila = filas[i]
    if (!fila || (fila.codigo || '') === (fila.sku || '')) return
    const base = i < renglones.length ? renglones : [...renglones, { ...renglonEnBlanco(), ...fila }]
    actualizar(await resolverCodigo(base, i, fila.codigo))
  }

  // Del proveedor, por artículo y SOLO en celdas vacías (lo tipeado manda): el código con que lo conoce
  // y, de su lista de precios VIGENTE, precio de lista y Dto1-3. Una sola consulta combinada para que
  // las dos respuestas no se pisen al actualizar la grilla.
  useEffect(() => {
    if (!proveedorId || soloLectura) return
    const clave = (s) => `${proveedorId}:${listaVersion}:${s}`
    const faltan = renglones.filter(r => r.sku && !pedidos.current.has(clave(r.sku)) && (!r.codigo_proveedor || r.precio_lista === '')).map(r => r.sku)
    if (faltan.length === 0) return
    faltan.forEach(s => pedidos.current.add(clave(s)))
    const skus = [...new Set(faltan)]
    Promise.all([
      npApi.codigosProveedor(proveedorId, skus).then(({ data }) => data.codigos || {}).catch(() => ({})),
      listasProveedorApi.preciosVigentes(proveedorId, skus).then(({ data }) => data.precios || {}).catch(() => ({})),
    ]).then(([cods, precios]) => {
      if (Object.keys(cods).length === 0 && Object.keys(precios).length === 0) return
      const pct = (v) => (Number(v) ? String(Number(v)) : '')
      onChange(renglones.map(r => {
        if (!r.sku) return r
        const p = precios[String(r.sku).toUpperCase()]
        const conPrecio = p && r.precio_lista === ''
        const cod = !r.codigo_proveedor && cods[r.sku]
        if (!conPrecio && !cod) return r
        return {
          ...r,
          ...(cod ? { codigo_proveedor: cods[r.sku] } : {}),
          ...(conPrecio ? {
            precio_lista: String(Number(p.precio_lista)),
            descuento1_pct: r.descuento1_pct || pct(p.descuento1_pct),
            descuento2_pct: r.descuento2_pct || pct(p.descuento2_pct),
            descuento3_pct: r.descuento3_pct || pct(p.descuento3_pct),
            origen_precio: 'lista_proveedor',
          } : {}),
        }
      }))
    })
  }, [proveedorId, listaVersion, renglones, onChange, soloLectura])

  // ---- teclado tipo Excel
  const focusCelda = (r, c) => {
    const el = gridRef.current?.querySelector(`input[data-r="${r}"][data-c="${c}"]`)
    if (el) { el.focus(); el.select?.() }
  }
  const onKeyDown = (e, r, c) => {
    const el = e.target
    const enInicio = el.selectionStart === 0 && el.selectionEnd === 0
    const enFin = el.selectionStart === el.value.length
    const idx = EDITABLES.indexOf(c)
    if (e.key === 'ArrowDown' || e.key === 'Enter') { e.preventDefault(); if (r < filas.length - 1) focusCelda(r + 1, c); else el.blur() }
    else if (e.key === 'ArrowUp') { e.preventDefault(); if (r > 0) focusCelda(r - 1, c) }
    else if (e.key === 'ArrowRight' && (enFin || el.value === '')) {
      e.preventDefault()
      if (idx < EDITABLES.length - 1) focusCelda(r, EDITABLES[idx + 1]); else if (r < filas.length - 1) focusCelda(r + 1, EDITABLES[0])
    } else if (e.key === 'ArrowLeft' && (enInicio || el.value === '')) {
      e.preventDefault()
      if (idx > 0) focusCelda(r, EDITABLES[idx - 1]); else if (r > 0) focusCelda(r - 1, EDITABLES[EDITABLES.length - 1])
    }
  }

  // ---- pegar desde Excel: matriz de celdas que cae desde (r, c) en el orden de la grilla.
  const onPaste = async (e, r0, c0) => {
    const texto = e.clipboardData?.getData('text') || ''
    if (!/[\t\n]/.test(texto)) return                  // un solo valor: pegado normal
    e.preventDefault()
    const matriz = texto.replace(/\r/g, '').split('\n').filter(l => l.trim() !== '').map(l => l.split('\t'))
    let nuevas = [...renglones]
    while (nuevas.length < r0 + matriz.length) nuevas.push(renglonEnBlanco())
    const aResolver = []
    matriz.forEach((celdas, k) => {
      const i = r0 + k
      const fila = { ...nuevas[i] }
      celdas.forEach((v, j) => {
        const col = COLS[c0 + j]
        if (!col || !col.edit) return
        const val = col.tipo === 'texto' ? String(v).trim() : normalizarNumero(v)
        fila[col.k] = val
        if (col.k === 'codigo' && val.toUpperCase() !== String(fila.sku || '').toUpperCase()) aResolver.push(i)
      })
      nuevas[i] = fila
    })
    for (const i of aResolver) nuevas = await resolverCodigo(nuevas, i, nuevas[i].codigo)
    actualizar(nuevas.filter((f, i) => !renglonVacio(f) || i < renglones.length))
  }

  const tot = totalesRenglones(renglones, bonifPct)
  const celdaInput = (r, c, fila) => {
    const col = COLS[c]
    const conError = col.k === 'codigo' && fila.error
    return (
      <input
        data-r={r} data-c={c}
        style={{ ...st.inputCelda, width: col.w, textAlign: col.tipo === 'texto' ? 'left' : 'right', fontFamily: col.mono || col.tipo !== 'texto' ? font.mono : 'inherit', borderColor: conError ? colors.err : colors.border }}
        value={fila[col.k] ?? ''}
        inputMode={col.tipo === 'texto' ? 'text' : 'decimal'}
        placeholder={col.k === 'codigo' && r === filas.length - 1 ? 'código…' : ''}
        title={conError || undefined}
        onChange={e => setFila(r, { [col.k]: limpiarTipeo(col.tipo, e.target.value) })}
        onBlur={col.k === 'codigo' ? () => alSalirDeCodigo(r) : undefined}
        onKeyDown={e => onKeyDown(e, r, c)}
        onPaste={e => onPaste(e, r, c)}
        disabled={soloLectura}
      />
    )
  }

  return (
    <div>
      {!soloLectura && (
        <div onKeyDown={e => { if (e.key === 'F2') { e.preventDefault(); setBuscador(true) } }}>
          {/* Mismos accesos que la NV: buscar varios, pegar el pedido y el buscador en línea. */}
          <div style={{ display: 'flex', gap: space.sm, justifyContent: 'flex-end', marginBottom: space.sm }}>
            <Boton variante="secondary" sm onClick={() => setBuscador(true)} title="Buscar y agregar varios artículos (F2)">Buscar artículos</Boton>
            <Boton variante="secondary" sm onClick={() => setPegar(true)} title="Pegá el texto del pedido y la IA arma los renglones">📋 Pegar pedido</Boton>
            <Boton variante="secondary" sm onClick={() => focusCelda(filas.length - 1, 0)}>+ Renglón</Boton>
          </div>
          <BuscadorArticulo onSelect={agregar} />
        </div>
      )}
      {pegar && <ModalPegarPedido clienteId={null} onListo={alInterpretarPedido} onClose={() => setPegar(false)} />}
      {buscador && (
        <SelectorArticulosModal
          onConfirm={(sel) => { agregarVarios(sel); setIaResultado(null) }}
          onClose={() => { setBuscador(false); setIaResultado(null) }}
          preseleccion={iaResultado?.preseleccion}
          avisos={iaResultado?.avisos}
        />
      )}

      <div ref={gridRef} style={{ ...st.tableWrap, marginTop: space.md }}>
        <table style={{ ...st.table, minWidth: 1280 }}>
          <thead>
            <tr>
              {COLS.map(col => <th key={col.k} style={{ ...st.th, textAlign: col.tipo === 'texto' || !col.edit && !col.calc ? 'left' : 'right', minWidth: col.minW }}>{col.label}</th>)}
              {!soloLectura && <th style={{ ...st.th, width: 32 }} />}
            </tr>
          </thead>
          <tbody>
            {filas.map((fila, r) => {
              const c = calcularRenglon(fila, bonifPct)
              const enBlanco = r === filas.length - 1 && !soloLectura && renglonVacio(fila)
              return (
                <tr key={r} style={{ ...st.tr, opacity: enBlanco ? 0.7 : 1 }}>
                  {COLS.map((col, ci) => {
                    if (col.edit) return <td key={col.k} style={{ ...st.td, padding: `${space.xs}px ${space.sm}px` }}>{celdaInput(r, ci, fila)}</td>
                    if (col.calc) return <td key={col.k} style={{ ...st.tdImporte, padding: `${space.xs}px ${space.md}px`, color: fila.sku ? colors.text : colors.textMeta }}>{fila.sku ? fmtMoneda(c[col.calc], moneda, col.dec) : ''}</td>
                    return (
                      <td key={col.k} style={{ ...st.td, padding: `${space.xs}px ${space.md}px`, minWidth: col.minW }}>
                        {fila.error ? <span style={{ color: colors.err, fontSize: text.sec }}>{fila.error}</span> : fila.descripcion || (enBlanco ? <span style={{ color: colors.textMeta, fontSize: text.sec }}>tipeá un código o pegá desde Excel</span> : '')}
                      </td>
                    )
                  })}
                  {!soloLectura && <td style={st.td}>{!enBlanco && <button type="button" style={st.quitar} onClick={() => quitar(r)} aria-label="Quitar renglón">×</button>}</td>}
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={COLS.length} style={{ ...st.td, textAlign: 'right', color: colors.textSec, fontSize: text.sec, lineHeight: 1.8 }}>
                Neto {Number(bonifPct) ? <span style={{ color: colors.textMeta }}>(bonif. general {bonifPct}%) </span> : null}<b style={{ fontFamily: font.mono, color: colors.text }}>{fmtMoneda(tot.neto, moneda)}</b>
                <span style={{ margin: `0 ${space.md}px` }}>·</span>IVA <b style={{ fontFamily: font.mono, color: colors.text }}>{fmtMoneda(tot.iva, moneda)}</b>
                {tot.internos ? <><span style={{ margin: `0 ${space.md}px` }}>·</span>Internos <b style={{ fontFamily: font.mono, color: colors.text }}>{fmtMoneda(tot.internos, moneda)}</b></> : null}
                <span style={{ margin: `0 ${space.md}px` }}>·</span>TOTAL <b style={{ fontFamily: font.mono, color: colors.text, fontSize: text.title }}>{fmtMoneda(tot.final, moneda)}</b>
              </td>
              {!soloLectura && <td />}
            </tr>
          </tfoot>
        </table>
      </div>
      {!soloLectura && (
        <p style={{ ...st.info, marginTop: space.sm, fontSize: text.sec, color: colors.textMeta, borderRadius: radius.sm }}>
          Grilla tipo Excel: flechas para moverse, Enter baja de renglón. Podés <b>pegar desde Excel</b>: las columnas caen en el mismo orden de la grilla desde la celda donde estás parado (Detalle, Precio y Total se ignoran porque se calculan).
          Cód. proveedor y EAN que cambies acá actualizan el maestro de artículos.
        </p>
      )}
    </div>
  )
}
